import { useEffect, useMemo, useState } from "react";
import client from "../lib/api";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, ReferenceLine, Tooltip } from "recharts";

const RANGES = [
  { key: "30", days: 30 },
  { key: "60", days: 60 },
  { key: "90", days: 90 },
];

export default function PriceChart({ ticker, filingDate, kind, currency }) {
  const [series, setSeries] = useState([]);
  const [range, setRange] = useState("90");
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    setLoading(true);
    client.get(`/prices/${encodeURIComponent(ticker)}`, { params: { days: 120 } })
      .then(r => setSeries(r.data.series || []))
      .catch(() => setSeries([]))
      .finally(() => setLoading(false));
  }, [ticker]);

  const filtered = useMemo(() => {
    const spec = RANGES.find(r => r.key === range);
    if (!spec) return series;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - spec.days);
    return series.filter(p => new Date(p.date) >= cutoff);
  }, [series, range]);

  const displayed = hover || filtered[filtered.length - 1] || { close: 0, date: "—" };
  const baseline = filtered[0]?.close ?? 0;
  const changePct = baseline ? (displayed.close / baseline - 1) * 100 : 0;
  const isUp = changePct >= 0;
  const lineColor = isUp ? "#5eead4" : "#f38fb2";
  const gradId = isUp ? "px-up-grad" : "px-down-grad";
  const cur = currency === "AUD" ? "A$" : "$";

  return (
    <div className="glass p-6" data-testid="price-chart">
      <div className="flex items-start justify-between gap-6 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)] font-medium">Share price · {ticker}</div>
          <div className="mt-2 flex items-baseline gap-3">
            <div className="mono text-[32px] font-semibold tracking-tight leading-none" data-testid="price-value">{cur}{displayed.close?.toFixed(2)}</div>
            <div className="mono text-[13px] font-semibold" style={{ color: lineColor }} data-testid="price-change">
              {isUp ? "▲" : "▼"} {isUp ? "+" : ""}{changePct.toFixed(2)}%
            </div>
          </div>
          <div className="mt-1 mono text-[11px] text-[color:var(--text-tertiary)]" data-testid="price-hover-info">
            {displayed.date} · {range}-day range · baseline {cur}{baseline?.toFixed(2)}
          </div>
        </div>
        <div className="flex items-center gap-1 p-1 rounded-full bg-black/25 border border-white/[0.05]" data-testid="price-range">
          {RANGES.map(r => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              data-testid={`price-range-${r.key}`}
              className={`mono text-[11px] px-3 py-1.5 rounded-full font-medium transition-colors ${range === r.key ? "text-[#0f1420]" : "text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]"}`}
              style={range === r.key ? { background: "linear-gradient(135deg, var(--violet), var(--cyan))" } : {}}
            >{r.key}D</button>
          ))}
        </div>
      </div>

      <div style={{ height: 220, position: "relative" }} data-testid="price-svg-wrap">
        {loading ? (
          <div className="mono text-[color:var(--text-tertiary)] terminal-loader h-full flex items-center">Fetching yfinance data</div>
        ) : filtered.length === 0 ? (
          <div className="mono text-[color:var(--text-tertiary)] h-full flex items-center justify-center">No price data available for {ticker}</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={filtered} margin={{ top: 8, right: 4, bottom: 4, left: 4 }} onMouseLeave={() => setHover(null)}>
              <defs>
                <linearGradient id="px-up-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#5eead4" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#5eead4" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="px-down-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f38fb2" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#f38fb2" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" hide />
              <YAxis domain={["dataMin", "dataMax"]} hide />
              {filingDate && filtered.some(p => p.date >= filingDate) && (
                <ReferenceLine
                  x={filingDate}
                  stroke="#f5df7c"
                  strokeDasharray="4 3"
                  strokeWidth={1.5}
                  label={{ value: `SIGNAL ${filingDate}`, position: "top", fill: "#f5df7c", fontSize: 9, fontFamily: "Geist Mono", offset: 6 }}
                />
              )}
              <Tooltip
                cursor={{ stroke: lineColor, strokeWidth: 1, strokeDasharray: "3 3" }}
                content={<PriceTooltip setHover={setHover} lineColor={lineColor} cur={cur} />}
              />
              <Area type="monotone" dataKey="close" stroke={lineColor} strokeWidth={2} fill={`url(#${gradId})`} isAnimationActive={false} activeDot={{ r: 5, fill: lineColor, stroke: "#0f1420", strokeWidth: 2 }} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {filingDate && (
        <div className="mt-3 flex items-center gap-2 mono text-[10px] text-[color:var(--text-tertiary)]" data-testid="price-legend">
          <span className="inline-block w-3 h-px" style={{ background: "#f5df7c" }} />
          <span>Dashed line marks the {kind?.includes("sell") ? "sell" : "buy"} signal filing date</span>
          <span className="ml-auto">Live yfinance · adjusted close</span>
        </div>
      )}
    </div>
  );
}

function PriceTooltip({ active, payload, setHover, lineColor, cur }) {
  if (active && payload?.length) {
    const p = payload[0].payload;
    setTimeout(() => setHover(p), 0);
    return (
      <div className="rounded-xl px-3 py-2 border" style={{ background: "rgba(15,20,32,0.95)", borderColor: "rgba(255,255,255,0.10)", backdropFilter: "blur(12px)" }}>
        <div className="mono text-[11px] text-[color:var(--text-tertiary)]">{p.date}</div>
        <div className="mono text-[15px] font-semibold" style={{ color: lineColor }}>{cur}{p.close.toFixed(2)}</div>
      </div>
    );
  }
  return null;
}
